import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for debounced search input
 * @param {string} initialValue - Initial search value
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Array} [debouncedValue, actualValue, setValue]
 */
export const useDebounce = (initialValue = '', delay = 300) => {
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return [debouncedValue, value, setValue];
};

/**
 * Custom hook for search functionality
 * Combines local and API data, filters them based on search query
 * @param {Array} localData - Local static data
 * @param {Function} fetchApiData - Function to fetch API data
 * @returns {Object} Search state and functions
 */
export const useSearch = (localData = [], fetchApiData = null) => {
  const [apiData, setApiData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, actualQuery, setSearchQuery] = useDebounce('', 300);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const isMounted = useRef(true);

  // Fetch API data once on mount
  useEffect(() => {
    const loadApiData = async () => {
      if (!fetchApiData) {
        setApiData([]);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchApiData();
        if (isMounted.current) {
          setApiData(data || []);
        }
      } catch (err) {
        if (isMounted.current) {
          setError(err.message || 'Failed to load data');
          console.error('Search data fetch error:', err);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadApiData();

    return () => {
      isMounted.current = false;
    };
  }, [fetchApiData]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    // Combine both data sources
    const allData = [...localData, ...apiData];

    // Filter data based on search query
    const filteredResults = filterData(allData, searchQuery);

    setResults(filteredResults);
    setIsSearching(false);
  }, [searchQuery, localData, apiData]);

  return {
    searchQuery: actualQuery,
    setSearchQuery,
    results,
    isLoading,
    isSearching,
    error,
    hasQuery: searchQuery.trim().length > 0
  };
};

/**
 * Filter data based on search query
 * Searches in title, description, and keywords
 * @param {Array} data - Array of search items
 * @param {string} query - Search query
 * @returns {Array} Filtered results
 */
export const filterData = (data, query) => {
  if (!query || !query.trim()) return [];

  const searchTerms = query.toLowerCase().trim().split(' ').filter(Boolean);

  return data.filter(item => {
    const searchableText = [
      item.title || '',
      item.description || '',
      item.category || '',
      ...(item.keywords || [])
    ].join(' ').toLowerCase();

    // Item matches if ALL search terms are found
    return searchTerms.every(term => searchableText.includes(term));
  }).sort((a, b) => {
    // Sort by relevance: title matches first
    const aTitle = (a.title || '').toLowerCase();
    const bTitle = (b.title || '').toLowerCase();
    const queryLower = query.toLowerCase();

    const aStartsWith = aTitle.startsWith(queryLower);
    const bStartsWith = bTitle.startsWith(queryLower);

    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;

    const aIncludes = aTitle.includes(queryLower);
    const bIncludes = bTitle.includes(queryLower);

    if (aIncludes && !bIncludes) return -1;
    if (!aIncludes && bIncludes) return 1;

    return 0;
  });
};

/**
 * Highlight matching text in search results
 * @param {string} text - Text to highlight
 * @param {string} query - Search query
 * @returns {JSX.Element} Text with highlighted matches
 */
export const highlightMatch = (text, query) => {
  if (!query || !text) return text;

  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  
  return (
    <>
      {parts.map((part, index) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} className="bg-[#FFD6A5] text-[#333333] font-semibold px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </>
  );
};
